/**
 * A dependency-free AppSync Events client: the WebSocket protocol boxed's raw test client speaks
 * (`services/test/battleEvents.test.ts`), without Amplify. Connect with a realtime token the
 * platform minted, subscribe to channels, get each event as parsed JSON. Reconnects with capped
 * exponential backoff, re-mints the token when it is about to expire or the server refuses it,
 * and re-subscribes everything after a reconnect.
 */

export type RealtimeState = "idle" | "connecting" | "open" | "reconnecting" | "closed";

export interface RealtimeAuth {
  /** The Event API's HTTP hostname: the `host` in the auth header. */
  httpEndpoint: string;
  /** The Event API's realtime hostname: where the socket connects. */
  realtimeEndpoint: string;
  token: string;
  expiresAtMs: number;
}

/** The subset of a WebSocket this client drives; the real one fits, and a fake is easy. */
export interface WebSocketLike {
  send(data: string): void;
  close(): void;
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: (() => void) | null;
  onerror: (() => void) | null;
}

export interface RealtimeClientOptions {
  /** The current token, or a freshly minted one when `fresh` (expiry, or the server refused it). */
  getAuth: (opts: { fresh: boolean }) => Promise<RealtimeAuth>;
  createSocket?: (url: string, protocols: string[]) => WebSocketLike;
  backoff?: { baseMs?: number; maxMs?: number };
  /**
   * Something worth looking into: a refused subscription, an error frame, a token that could not
   * be minted, or the server still unreachable after `UNREACHABLE_AFTER_ATTEMPTS` connects.
   */
  onError?: (error: unknown) => void;
  /**
   * The socket's own `error` event. A browser attaches no cause to it and fires it on every
   * dropped connection (sleep, a network change, the server recycling the socket); the close that
   * follows reconnects, so on its own it is not an error.
   */
  onSocketError?: () => void;
  now?: () => number;
}

/**
 * Where one subscription stands: `pending` until the server acknowledges it (and again after a
 * drop, until the re-subscribe is acknowledged), `live` while events flow, `refused` when the
 * server rejected it. Only `live` means a page may stop polling; a connected socket is not enough.
 */
export type SubscriptionStatus = "pending" | "live" | "refused";

export interface RealtimeClient {
  /** Subscribes (connecting lazily); the returned function unsubscribes. */
  subscribe(
    channel: string,
    onEvent: (event: unknown) => void,
    onStatus?: (status: SubscriptionStatus) => void,
  ): () => void;
  /**
   * Reconnects with a token minted afresh, re-subscribing everything: the viewer the token names
   * changed (a shopper signed in or out). A no-op while nothing is connected.
   */
  reauthenticate(): void;
  getState(): RealtimeState;
  onStateChange(listener: (state: RealtimeState) => void): () => void;
  close(): void;
}

export type RealtimeMessage =
  | { type: "connection_ack"; connectionTimeoutMs?: number }
  | { type: "ka" }
  | { type: "subscribe_success"; id: string }
  | { type: "subscribe_error"; id: string; errors?: { errorType?: string; message?: string }[] }
  | { type: "unsubscribe_success"; id: string }
  | { type: "data"; id: string; event: string }
  | { type: "connection_error"; errors?: { errorType?: string; message?: string }[] }
  | { type: "error"; id?: string; errors?: { errorType?: string; message?: string }[] };

/** Base64url without padding, over UTF-8 bytes. */
export function base64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** The `header-…` subprotocol carrying the auth object AppSync reads on connect. */
export function encodeAuthProtocol(auth: { host: string; Authorization: string }): string {
  return `header-${base64Url(JSON.stringify(auth))}`;
}

/** Capped exponential backoff: base × 2^attempt, at most max. */
export function backoffDelayMs(attempt: number, baseMs = 1000, maxMs = 30_000): number {
  return Math.min(baseMs * 2 ** Math.max(0, attempt), maxMs);
}

export function parseRealtimeMessage(raw: unknown): RealtimeMessage | null {
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw) as { type?: unknown };
    return typeof parsed.type === "string" ? (parsed as RealtimeMessage) : null;
  } catch {
    return null;
  }
}

/** A refused connection or subscription whose cause is the token, not the channel. */
function isAuthError(errors: { errorType?: string }[] | undefined): boolean {
  return (errors ?? []).some((e) => /unauthor|token|expired/i.test(e.errorType ?? ""));
}

const REFRESH_SLACK_MS = 60_000;

/**
 * Failed connects in a row, with no acknowledged subscription between them, after which the
 * client reports itself unreachable through `onError`, once per outage. With the default backoff
 * that is about 30 seconds of trying.
 */
export const UNREACHABLE_AFTER_ATTEMPTS = 5;

interface Subscription {
  channel: string;
  onEvent: (event: unknown) => void;
  onStatus?: (status: SubscriptionStatus) => void;
  status: SubscriptionStatus;
}

export function createRealtimeClient(options: RealtimeClientOptions): RealtimeClient {
  const createSocket =
    options.createSocket ??
    ((url, protocols) => new WebSocket(url, protocols) as unknown as WebSocketLike);
  const now = options.now ?? (() => Date.now());
  const baseMs = options.backoff?.baseMs ?? 1000;
  const maxMs = options.backoff?.maxMs ?? 30_000;

  const subscriptions = new Map<string, Subscription>();
  const stateListeners = new Set<(state: RealtimeState) => void>();
  let state: RealtimeState = "idle";
  let socket: WebSocketLike | null = null;
  let auth: RealtimeAuth | null = null;
  let needFreshAuth = false;
  let attempt = 0;
  let reportedUnreachable = false;
  let closed = false;
  let connecting = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let watchdog: ReturnType<typeof setTimeout> | null = null;
  let watchdogMs = 300_000;
  // Bumped by `reauthenticate`: a token minted under an older value names the previous viewer.
  let authEpoch = 0;

  function setState(next: RealtimeState) {
    if (state === next) return;
    state = next;
    for (const listener of stateListeners) listener(next);
  }

  function newId(): string {
    return typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }

  function armWatchdog() {
    if (watchdog) clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      // No keep-alive inside the server's window: the socket is dead even if it never said so.
      socket?.close();
      handleClosed();
    }, watchdogMs);
  }

  function authHeader(current: RealtimeAuth) {
    return { host: current.httpEndpoint, Authorization: current.token };
  }

  function setSubscriptionStatus(subscription: Subscription, next: SubscriptionStatus) {
    if (subscription.status === next) return;
    subscription.status = next;
    subscription.onStatus?.(next);
  }

  function sendSubscribe(id: string, subscription: Subscription) {
    if (!socket || !auth || state !== "open") return;
    setSubscriptionStatus(subscription, "pending");
    socket.send(
      JSON.stringify({
        type: "subscribe",
        id,
        channel: subscription.channel,
        authorization: authHeader(auth),
      }),
    );
  }

  async function connect() {
    if (closed || connecting || socket) return;
    connecting = true;
    setState(attempt === 0 ? "connecting" : "reconnecting");
    const epoch = authEpoch;
    let minted: RealtimeAuth;
    try {
      const stale = !auth || auth.expiresAtMs - REFRESH_SLACK_MS <= now();
      minted = await options.getAuth({ fresh: needFreshAuth || stale });
      needFreshAuth = false;
    } catch (error) {
      connecting = false;
      options.onError?.(error);
      scheduleReconnect();
      return;
    }
    if (closed) {
      connecting = false;
      return;
    }
    if (epoch !== authEpoch) {
      // The viewer changed while this token was minted: mint again for the new one.
      connecting = false;
      void connect();
      return;
    }
    auth = minted;
    const current = minted;
    const url = `wss://${current.realtimeEndpoint}/event/realtime`;
    let ws: WebSocketLike;
    try {
      ws = createSocket(url, [encodeAuthProtocol(authHeader(current)), "aws-appsync-event-ws"]);
    } catch (error) {
      connecting = false;
      options.onError?.(error);
      scheduleReconnect();
      return;
    }
    socket = ws;
    connecting = false;
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "connection_init" }));
    };
    ws.onmessage = (event) => {
      const message = parseRealtimeMessage(event.data);
      if (!message) return;
      armWatchdog();
      switch (message.type) {
        case "connection_ack":
          if (message.connectionTimeoutMs) watchdogMs = message.connectionTimeoutMs;
          armWatchdog();
          setState("open");
          for (const [id, subscription] of subscriptions) sendSubscribe(id, subscription);
          break;
        case "ka":
          break;
        case "subscribe_success": {
          const subscription = subscriptions.get(message.id);
          if (subscription) setSubscriptionStatus(subscription, "live");
          // An acknowledged subscription, not the connection ack, is what resets the backoff:
          // a server that connects us and then refuses every subscribe must not be retried
          // every second.
          attempt = 0;
          reportedUnreachable = false;
          break;
        }
        case "subscribe_error": {
          const subscription = subscriptions.get(message.id);
          if (subscription) setSubscriptionStatus(subscription, "refused");
          if (isAuthError(message.errors)) {
            needFreshAuth = true;
            ws.close();
            handleClosed();
          } else {
            options.onError?.(new Error(`subscribe refused: ${JSON.stringify(message.errors)}`));
          }
          break;
        }
        case "connection_error":
        case "error":
          if (isAuthError(message.errors)) needFreshAuth = true;
          options.onError?.(new Error(`realtime error: ${JSON.stringify(message.errors)}`));
          break;
        case "data": {
          const subscription = subscriptions.get(message.id);
          if (!subscription) break;
          try {
            subscription.onEvent(JSON.parse(message.event));
          } catch (error) {
            options.onError?.(error);
          }
          break;
        }
        case "unsubscribe_success":
          break;
      }
    };
    ws.onerror = () => {
      options.onSocketError?.();
    };
    ws.onclose = () => handleClosed();
  }

  function handleClosed() {
    if (watchdog) {
      clearTimeout(watchdog);
      watchdog = null;
    }
    socket = null;
    // Events stopped flowing; a refused subscription stays refused until its next attempt.
    for (const subscription of subscriptions.values()) {
      if (subscription.status === "live") setSubscriptionStatus(subscription, "pending");
    }
    if (closed) {
      setState("closed");
      return;
    }
    if (subscriptions.size === 0) {
      setState("idle");
      return;
    }
    scheduleReconnect();
  }

  function scheduleReconnect() {
    if (closed || reconnectTimer) return;
    setState("reconnecting");
    const delay = backoffDelayMs(attempt, baseMs, maxMs);
    attempt += 1;
    if (attempt >= UNREACHABLE_AFTER_ATTEMPTS && !reportedUnreachable) {
      reportedUnreachable = true;
      options.onError?.(new Error(`realtime unreachable after ${attempt} attempts`));
    }
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, delay);
  }

  return {
    subscribe(channel, onEvent, onStatus) {
      const id = newId();
      const subscription: Subscription = { channel, onEvent, onStatus, status: "pending" };
      subscriptions.set(id, subscription);
      if (state === "open") sendSubscribe(id, subscription);
      else void connect();
      return () => {
        if (!subscriptions.delete(id)) return;
        if (socket && state === "open") {
          socket.send(JSON.stringify({ type: "unsubscribe", id }));
        }
        if (subscriptions.size === 0 && socket) {
          const ws = socket;
          socket = null;
          ws.onclose = null;
          ws.close();
          if (watchdog) {
            clearTimeout(watchdog);
            watchdog = null;
          }
          setState("idle");
        }
      };
    },
    reauthenticate() {
      if (closed) return;
      authEpoch += 1;
      auth = null;
      const ws = socket;
      // Not connected: the next connect, or the one minting now (see `connect`), mints afresh.
      if (!ws) return;
      socket = null;
      ws.onclose = null;
      ws.close();
      if (watchdog) {
        clearTimeout(watchdog);
        watchdog = null;
      }
      for (const subscription of subscriptions.values()) {
        if (subscription.status === "live") setSubscriptionStatus(subscription, "pending");
      }
      if (subscriptions.size === 0) {
        setState("idle");
        return;
      }
      attempt = 0;
      void connect();
    },
    getState: () => state,
    onStateChange(listener) {
      stateListeners.add(listener);
      return () => stateListeners.delete(listener);
    },
    close() {
      closed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (watchdog) {
        clearTimeout(watchdog);
        watchdog = null;
      }
      const ws = socket;
      socket = null;
      if (ws) {
        ws.onclose = null;
        ws.close();
      }
      subscriptions.clear();
      setState("closed");
    },
  };
}
